import { ReactNode, useMemo } from "react";
import { Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { col6 } from "../../../../../../../../common/utils";
import { useFiltersRegistry } from "../../../../../../../../context/FiltersProvider";
import {
	RootCauseListPayload,
	RootCauseWidgetEmissionFactorChangesDTO,
} from "../../../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../../../../hooks/useResource";
import { CardDelta } from "../../../../components/CardDelta";
import BaselineComparisonBarChart from "../../BaselineComparisonBarChart";
import { EffectEnergyCard } from "../EffectEnergyCard";
import { EnergyChartItem, mapFuelToEnergyChartItem, mapVehicleToEnergyChartItem } from "./data/mappersVehiclesFuels";

type Props = {
	route: ApiRoutes;
	transportType: string;
	title: string;
};

type EffectEnergyChangesItem = {
	title: string;
	text: string;
	deltaCard: ReactNode;
	circleChartTitle: string;
	yAxisTitle: string;
	barChartTitle: string;
	chartData: EnergyChartItem[];
};

export const ChangeEmissionTab = ({ route, transportType, title }: Props) => {
	const { t } = useTranslation("dataInsights.rootcause");
	const { rootCauseState } = useFiltersRegistry();

	const { data, isLoading } = useReadResource<RootCauseListPayload, RootCauseWidgetEmissionFactorChangesDTO>(
		{
			route,
		},
		{
			readMethod: "post",
		},
		{ ...rootCauseState.payload }
	);

	const vehicleChartData: EnergyChartItem[] = data?.vehicles?.map(mapVehicleToEnergyChartItem) ?? [];
	const fuelChartData: EnergyChartItem[] = data?.fuels?.map(mapFuelToEnergyChartItem) ?? [];

	const deltaValues = [
		{
			title: t("root cause results/changes in emissions/deltaValues/emission factor"),
			value: data?.co2.value ?? 0,
			measure: data?.co2.measure ?? "-",
		},
		{
			title: t("root cause results/changes in emissions/deltaValues/baseline"),
			value: data?.baseCo2Tkm.value ?? 0,
			measure: data?.baseCo2Tkm.measure ?? "-",
			maximumFractionDigits: 4,
		},
		{
			title: t("root cause results/changes in emissions/deltaValues/comparison"),
			value: data?.compCo2Tkm.value ?? 0,
			measure: data?.compCo2Tkm.measure ?? "-",
			maximumFractionDigits: 4,
		},
	];

	const baselineComparisonData = [
		{
			title: t("root cause results/changes in emissions/baseline comparison data/saturation"),
			baseline: data?.baseSat,
			comparison: data?.compSat,
		},
		{
			title: t("root cause results/changes in emissions/baseline comparison data/empty returns"),
			baseline: data?.baseRav,
			comparison: data?.compRav,
		},
		{
			title: t("root cause results/changes in emissions/baseline comparison data/refrigeration share"),
			baseline: data?.baseRefrigerant,
			comparison: data?.compRefrigerant,
		},
		{
			title: t("root cause results/changes in emissions/baseline comparison data/FTL share"),
			baseline: data?.baseFtl,
			comparison: data?.compFtl,
		},
	];

	const effectEnergyChangesData: EffectEnergyChangesItem[] = [
		{
			title: t(
				"root cause results/changes in emissions/effect energy changes data/effects of energy consumption"
			),
			text: t(
				"root cause results/changes in emissions/effect energy changes data/effects of energy consumption text"
			),
			deltaCard: (
				<CardDelta
					title={t("root cause results/changes in emissions/total/energy usage")}
					value={data?.co2MjTransport.value ?? "0"}
					measure={data?.co2MjTransport.measure ?? "-"}
					isLoading={isLoading}
				/>
			),
			circleChartTitle: t(
				"root cause results/changes in emissions/effect energy changes data/vehicle and scenario"
			),
			yAxisTitle: t("root cause results/changes in emissions/chart labels/energy intensity axis"),
			barChartTitle:
				t("root cause results/changes in emissions/effect energy changes data/vehicle category") +
				" - " +
				transportType,
			chartData: vehicleChartData,
		},
		{
			title: t("root cause results/changes in emissions/effect energy changes data/energy carriers mix"),
			text: t("root cause results/changes in emissions/effect energy changes data/energy carriers mix text"),
			deltaCard: (
				<CardDelta
					title={t("root cause results/changes in emissions/total/energy carriers mix")}
					value={data?.co2MjFuel.value ?? "0"}
					measure={data?.co2MjFuel.measure ?? "-"}
					isLoading={isLoading}
				/>
			),
			circleChartTitle: t("root cause results/changes in emissions/effect energy changes data/fuel and scenario"),
			yAxisTitle: t("root cause results/changes in emissions/chart labels/emissions intensity axis"),
			barChartTitle:
				t("root cause results/changes in emissions/effect energy changes data/fuels by TKM") +
				" - " +
				transportType,
			chartData: fuelChartData,
		},
	];

	return (
		<Stack gap={2}>
			<CardDelta title={title} values={deltaValues} isLoading={isLoading} />

			<Grid container spacing={2}>
				{baselineComparisonData.map((chart) => (
					<Grid size={col6} key={chart.title}>
						<BaselineComparisonBarChart
							title={chart.title}
							type={transportType}
							baseline={chart.baseline}
							comparison={chart.comparison}
							baselineLabel={t(
								"root cause results/changes in emissions/chart labels/baseline"
							)}
							comparisonLabel={t(
								"root cause results/changes in emissions/chart labels/comparison"
							)}
							isLoading={isLoading}
						/>
					</Grid>
				))}

				{effectEnergyChangesData.map((card) => (
					<Grid size={col6} key={card.title}>
						<EffectEnergyCard
							title={card.title}
							text={card.text}
							deltaCard={card.deltaCard}
							circleChartTitle={card.circleChartTitle}
							yAxisTitle={card.yAxisTitle}
							chartData={card.chartData}
							barChartTitle={card.barChartTitle}
							isLoading={isLoading}
						/>
					</Grid>
				))}
			</Grid>
		</Stack>
	);
};
